import { ConfigurationSourceDataMap, ConfigurationStatus, CoordinatingGroup, Institution, MasterGroup, Metadata, Note, NoteDetail, NoteStatus, Participant, SourceData, Trial } from "@/api/dtos";
import noteStatusToText from "@/utilities/configuration/noteStatusToText";
import { Box, Button, Chip, Divider, ListItem, ListItemIcon, ListItemText, Paper, Theme, alpha, styled } from "@mui/material";
import { DateTime } from "luxon";
import { FC, useCallback, useMemo, useState } from "react";
import NoteDetails from "./NoteDetails";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import noteTypeToText from "@/utilities/configuration/noteTypeToText";
import { StatusListItemTooltip } from "../status/StatusListStats";
import { AppSettings } from "@/AppSettings";
import noteStatusToColor from "@/utilities/configuration/noteStatusToColor";
import NoteEditForm from "@/components/forms/forms/notes/NoteEditForm";


const NoteContainer = styled(Paper)(({ theme }) => ({
    marginBottom: theme.spacing(2)
}));

const NoteBox = styled(Box)(({ theme }) => ({
}));

const ChipBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    marginLeft: 'auto'
}));

const StatusChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(3),
    minWidth: theme.spacing(18.75)
}));

const DateChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(3),
    minWidth: theme.spacing(15)
}));

const UserChip = styled(Chip)(({ theme }) => ({
    height: theme.spacing(3),
    minWidth: theme.spacing(15)
}));

const IndentBox = styled(Box)(({ theme }) => ({
    marginLeft: theme.spacing(8)
}));

const IndentBorderBox = styled(Box)(({ theme }) => ({
    borderLeft: `1px solid ${alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12)}`
}));

const IndentDivider = styled(Divider)(({ theme }) => ({
    borderColor: alpha(theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.common.black, 0.12)
}));

const StyledListItem = styled(ListItem)(({ theme }) => ({
    padding: theme.spacing(0.5, 2),
    minHeight: theme.spacing(6),
    whiteSpace: 'pre-wrap'
}));

const StyledListItemText = styled(ListItemText)(({ theme }) => ({
    margin: theme.spacing(0.5, 0)
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
    minWidth: theme.spacing(6)
}));

const Buttons = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    padding: theme.spacing(2)

}));

interface ISubnoteViewProps extends Omit<INoteViewProps, 'data'> {
    data: NoteDetail;
    index: number;
    total: number;
}

const SubnoteView: FC<ISubnoteViewProps> = ({
    theme,
    trials,
    masterGroups,
    coordinatingGroups,
    institutions,
    participants,

    data,
    index,
    total
}) => {
    const status = noteStatusToText(data.status);
    const date = data.createdDate ? DateTime.fromISO(data.createdDate).toFormat('dd/MM/yyyy') : undefined;

    return (
        <IndentBox>

            {
                index !== 0 && (
                    <Divider />
                )
            }        
            <IndentBorderBox>
                <StyledListItem>
                    <StyledListItemText 
                        primary={data.title}
                    />
                    <ChipBox>
                        <StatusListItemTooltip
                            title={
                                <>
                                    <strong>Status</strong><br />
                                    {status}
                                </>
                            }
                        >
                            <StatusChip label={status} color={noteStatusToColor(data.status)} />
                        </StatusListItemTooltip>
                        {
                            date && (
                                <StatusListItemTooltip
                                    title={
                                        <>
                                            <strong>Date Created</strong><br />
                                            {date}
                                        </>
                                    }
                                >
                                    <DateChip icon={<FontAwesomeIcon icon={AppSettings.icons.itemValueDate} />} label={date} />
                                </StatusListItemTooltip>
                            )
                        }
                        <StatusListItemTooltip
                            title={
                                <>
                                    <strong>Created By</strong><br />
                                    {data.creatorFirstName} {data.creatorLastName}
                                </>
                            }
                        >
                            <UserChip icon={<FontAwesomeIcon icon={AppSettings.icons.user} />}  label={data.creatorUsername} />
                        </StatusListItemTooltip>
                    </ChipBox>
                </StyledListItem>
                    

                {
                    data.details && (
                        <Divider />
                    )
                }    
            </IndentBorderBox>
            {
                data.details && (
                    <IndentBox>
                        <IndentBorderBox>
                            <StyledListItem>
                                {data.details}
                            </StyledListItem>
                        </IndentBorderBox>
                    </IndentBox>
                )
            }
        </IndentBox>
    )
}

interface INoteViewProps {
    trialId?: number;
    masterGroupId?: number;
    coordinatingGroupId?: number;
    institutionId?: number;
    participantId?: number;
    studyEventOid?: string;
    studyEventRepeat?: number;
    caseReportFormOid?: string;
    caseReportFormVersionOid?: string;
    itemGroupName?: string;
    itemGroupRepeat?: number;
    itemName?: string;

    theme: Theme;
    trials?: Record<number, Trial>;
    masterGroups?: Record<number, MasterGroup>;
    coordinatingGroups?: Record<number, CoordinatingGroup>;
    institutions?: Record<number, Institution>;
    participants?: Record<number, Participant>;
    metadata?: Metadata;
    trialConfiguration?: ConfigurationSourceDataMap;
    configuration?: ConfigurationSourceDataMap;
    sourceData?: SourceData;

    data: Note;

    informationOnly?: boolean;
}

const NoteView: FC<INoteViewProps> = ({
    data,
    informationOnly,
    ...context
}) => {
    
    const status = noteStatusToText(data.status);
    const date = data.createdDate ? DateTime.fromISO(data.createdDate).toFormat('dd/MM/yyyy') : undefined;

    const [ editStatus, setEditStatus ] = useState<NoteStatus>();
    const onEditCancel = useCallback(() => {
        setEditStatus(undefined);
    }, [setEditStatus]);

    const {
        theme,

        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,
        metadata,
        trialConfiguration,
        configuration,
        sourceData
    } = context;

    const contextStatus = useMemo(() => {
        if (data.participantId) {
            return participants?.[data.participantId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (data.institutionId) {
            return institutions?.[data.institutionId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (data.coordinatingGroupId) {
            return coordinatingGroups?.[data.coordinatingGroupId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (data.masterGroupId) {
            return masterGroups?.[data.masterGroupId]?.status ?? ConfigurationStatus.Disabled;
        }

        if (data.trialId) {
            return trials?.[data.trialId]?.status ?? ConfigurationStatus.Disabled;
        }
    }, [
        trials,
        masterGroups,
        coordinatingGroups,
        institutions,
        participants,

        data
    ])

    const showEdit = !informationOnly && contextStatus == ConfigurationStatus.Enabled && !!AppSettings.allowNotesAndQueriesEdit;

    const NoteWrapper = informationOnly ? NoteBox : NoteContainer;

    return (
        <NoteWrapper>
            <NoteDetails 
                trialId={data.trialId}
                masterGroupId={data.masterGroupId}
                coordinatingGroupId={data.coordinatingGroupId}
                institutionId={data.institutionId}
                participantId={data.participantId}
                studyEventOid={data.studyEventOid}
                studyEventRepeat={data.studyEventRepeat}
                caseReportFormOid={data.caseReportFormOid}
                caseReportFormVersionOid={data.caseReportFormVersionOid}
                itemGroupName={data.itemGroupName}
                itemGroupRepeat={data.itemGroupRepeat}
                itemName={data.itemName}
                itemValue={data.itemValue}

                trials={trials}
                masterGroups={masterGroups}
                coordinatingGroups={coordinatingGroups}
                institutions={institutions}
                participants={participants}
                metadata={metadata}
                trialConfiguration={trialConfiguration}
                configuration={configuration}
                sourceData={sourceData}

                hideTrial={!!context.trialId}
                hideMasterGroup={!!context.masterGroupId}
                hideCoordinatingGroup={!!context.coordinatingGroupId}
                hideInstitution={!!context.institutionId}
                hideParticipant={!!context.participantId}
                hideStudyEvent={!!context.studyEventOid}
                hideCaseReportForm={!!context.caseReportFormOid}
                hideItemGroup={!!context.itemGroupName}
                hideItem={!!context.itemName}
                    
                enableLinks={true}
            />
            <StyledListItem>
                <StyledListItemIcon>
                    <FontAwesomeIcon size="lg" icon={AppSettings.icons.note} />
                </StyledListItemIcon>
                <StyledListItemText 
                    primary={noteTypeToText(data.type)}
                />
                <ChipBox>
                    <StatusListItemTooltip
                        title={
                            <>
                                <strong>Status</strong><br />
                                {status}
                            </>
                        }
                    >
                        <StatusChip label={status} color={noteStatusToColor(data.status)} />
                    </StatusListItemTooltip>
                    {
                        date && (
                            <StatusListItemTooltip
                                title={
                                    <>
                                        <strong>Date Created</strong><br />
                                        {date}
                                    </>
                                }
                            >
                                <DateChip icon={<FontAwesomeIcon icon={AppSettings.icons.itemValueDate} />} label={date} />
                            </StatusListItemTooltip>
                        )
                    }
                    <StatusListItemTooltip
                        title={
                            <>
                                <strong>Created By</strong><br />
                                {data.creatorFirstName} {data.creatorLastName}
                            </>
                        }
                    >
                        <UserChip icon={<FontAwesomeIcon icon={AppSettings.icons.user} />}  label={data.creatorUsername} />
                    </StatusListItemTooltip>
                </ChipBox>
            </StyledListItem>
            <Divider />
            {
                data.details?.map((x, i) => (
                    <SubnoteView
                        key={i}
                        {...context}
                        index={i}
                        total={data.details!.length}
                        data={x}
                    />
                ))
            }
            {(showEdit || editStatus) && !!data.details && data.details.length > 0 && <Divider />}
            {
                !editStatus && showEdit && data.status && (AppSettings.noteActions[data.status].addAdditionalInformation || AppSettings.noteActions[data.status].resolutionProposed || AppSettings.noteActions[data.status].reset || AppSettings.noteActions[data.status].reopen || AppSettings.noteActions[data.status].close) && (
                    <Buttons>
                        {
                            data.status && AppSettings.noteActions[data.status].resolutionProposed && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => setEditStatus(NoteStatus.ResolutionProposed)}
                                >
                                    Resolution Proposed
                                </Button>
                            )
                        }
                        {
                                data.status && AppSettings.noteActions[data.status].addAdditionalInformation && (
                                    <Button
                                        color="primary"
                                        variant="contained"
                                        onClick={() => setEditStatus(data.status)}
                                    >
                                        Add Additional Information
                                    </Button>
                                )
                        }
                        {
                            data.status && AppSettings.noteActions[data.status].reset && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => setEditStatus(NoteStatus.New)}
                                >
                                    Reset
                                </Button>
                            )
                        }
                        {
                            data.status && AppSettings.noteActions[data.status].reopen && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => setEditStatus(NoteStatus.New)}
                                >
                                    Reopen
                                </Button>
                            )
                        }
                        {
                            data.status && AppSettings.noteActions[data.status].close && (
                                <Button
                                    color="primary"
                                    variant="contained"
                                    onClick={() => setEditStatus(NoteStatus.Closed)}
                                >
                                    Close
                                </Button>
                            )
                        }
                        {/* <Button
                            color="error"
                            variant="contained"
                        >
                            Delete
                        </Button> */}
                    </Buttons>
                )
            }
            {
                !!editStatus && (
                    <NoteEditForm
                        trialId={data.trialId}
                        masterGroupId={data.masterGroupId}
                        coordinatingGroupId={data.coordinatingGroupId}
                        institutionId={data.institutionId}
                        participantId={data.participantId}
                        studyEventOid={data.studyEventOid}
                        studyEventRepeat={data.studyEventRepeat}
                        caseReportFormOid={data.caseReportFormOid}
                        caseReportFormVersionOid={data.caseReportFormVersionOid}
                        itemGroupName={data.itemGroupName}
                        itemGroupRepeat={data.itemGroupRepeat}
                        itemName={data.itemName}

                        noteId={data.id}
                        status={editStatus}
                        onCancel={onEditCancel}
                    />
                )
            }
        </NoteWrapper>
    )
}

export default NoteView;